import React, { useState, useRef } from 'react';
import ModalWrapper from '../ModalWrapper';
import { createTestGroup, updateTestGroup } from '../../utils/api/testgroup_apis';
import { STATUS } from '../../utils/api/sharedfunction';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { enGB } from 'date-fns/locale';
import { TextField } from '@mui/material';
import { useSnackbar } from '../../utils/useSnackbar';
import { FormSchemas, FormWrapper, CommonFooter } from '../FormComponent';

const CreateTestGroupModal = ({ open, onClose, onSuccess, projectId, editMode = false, initialValues = {}, onEdit, existingTestGroups = [] }) => {
  const { showSnackbar } = useSnackbar();
  const [modalError, setModalError] = useState('');
  const [nameError, setNameError] = useState('');

  // Clear errors when modal opens
  React.useEffect(() => {
    if (open) {
      setModalError('');
      setNameError('');
    }
  }, [open]);
  const formRef = useRef(null);

  const handleFormSubmit = async () => {
    if (formRef.current) {
      console.log('Form ref exists, calling submit');
      await formRef.current.submit();
    } else {
      console.log('Form ref is null');
    }
  };

  const handleConfirmSubmit = async (formValues) => {
    console.log('Form submitted with values:', formValues);
    setModalError('');
    setNameError('');
    
    // Validate character limits
    if (formValues.name && formValues.name.length > 100) {
      setNameError('Group name cannot exceed 100 characters');
      return;
    }
    
    if (formValues.description && formValues.description.length > 500) {
      setModalError('Description cannot exceed 500 characters');
      return;
    }
    

    const trimmedName = formValues.name?.trim();
    if (trimmedName) {
      const isDuplicate = existingTestGroups.some(group => 
        group.name.toLowerCase() === trimmedName.toLowerCase() && 
        (!editMode || group.id !== initialValues.id)
      );
      
      if (isDuplicate) {
        setNameError('A test group with this name already exists in this project. Please choose a different name.');
        return;
      }
    }
    
    try {
      if (editMode) {
        const response = await updateTestGroup({
          id: initialValues.id,
          name: formValues.name,
          description: formValues.description || '',
          // next_scheduled_run: null, // We're not using this field for now
        });
        
        if (response.status === STATUS.SUCCESS) {
          showSnackbar(response.message || 'Test group updated successfully', 'success');
          if (onEdit) {
            onEdit({
              name: formValues.name,
              description: formValues.description || '',
            });
          }
          onClose();
        } else {
          setModalError(response.error || 'Failed to update test group');
        }
      } else {
        const response = await createTestGroup({
          name: formValues.name,
          description: formValues.description || '',
          // next_scheduled_run: null, // We're not using this field for now
        });
        
        if (response.status === STATUS.SUCCESS) {
          showSnackbar(response.message || 'Test group created successfully', 'success');
          if (onSuccess) onSuccess(response.data); // Pass the created test group data
          onClose();
        } else {
          setModalError(response.error || 'Failed to create test group');
        }
      }
    } catch (error) {
      console.error('Error in form submission:', error);
      setModalError(error?.message || (editMode ? 'Failed to update test group' : 'Failed to create test group'));
    }
  };

  const fields = [
    {
      name: 'name',
      type: 'text',
      label: 'Group Name',
      placeholder: 'Enter group name',
      required: true,
      autoFocus: true,
      error: modalError,
      // Component: (props) => (
      //   <TextField 
      //     {...props} 
      //     fullWidth 
      //     inputProps={{ maxLength: 100 }} // Limit to 100 characters
      //     helperText={nameError || `${(props.value || '').length}/100 characters`}
      //     error={!!nameError}
      //     onChange={(e) => {
      //       props.onChange(e);
      //       // Clear name error when user starts typing
      //       if (nameError) {
      //         setNameError('');
      //       }
      //     }}
      //   />
      // ),
    },
    {
      name: 'description',
      type: 'text',
      label: 'Description',
      placeholder: 'Enter description (optional)',
      required: false,
      multiline: true,
      rows: 3,
    },
    // {
    //   name: 'next_scheduled_run',
    //   label: 'Next Scheduled Run',
    //   Component: (props) => {
    //     // Ensure value is a Date or null
    //     let value = props.value;
    //     if (typeof value === 'string' && value) {
    //       value = new Date(value);
    //     }
    //     if (value && !(value instanceof Date) && isNaN(value)) {
    //       value = null;
    //     }
    //     return (
    //       <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
    //         <DateTimePicker
    //           {...props}
    //           value={value || null}
    //           onChange={(val) => props.onChange(val)}
    //           format="dd/MM/yyyy hh:mm a"
    //           ampm={true}
    //           slotProps={{
    //             textField: { fullWidth: true, error: props.error, helperText: props.helperText },
    //             popper: { container: document.body },
    //           }}
    //         />
    //       </LocalizationProvider>
    //     );
    //   },
    // },
  ];

  const buttons = [
    {
      label: 'Cancel',
      variant: 'outline',
      className: 'primary',
      onClick: onClose,
    },
    {
      label: editMode ? 'Save' : 'Create',
      variant: 'primary',
      className: 'white bg-blue600',
      onClick: handleFormSubmit,
    },
  ];

  return (
    <ModalWrapper
      isOpen={open}
      onClose={onClose}
      title={editMode ? 'Edit Test Group' : 'Create New Test Group'}
      size="small"
      footer={
        <CommonFooter
          buttons={buttons}
          align="right"
        />
      }
    >
      {/* {modalError && <div style={{ color: 'red', marginBottom: 8 }}>{modalError}</div>} */}
      <FormWrapper
        ref={formRef}
        schema={FormSchemas.testGroupForm(existingTestGroups, editMode, initialValues)}
        fields={fields}
        onSubmit={handleConfirmSubmit}
        defaultValues={
          editMode
            ? {
                name: initialValues.name || '',
                description: initialValues.description || '',
              }
            : {
                name: initialValues.name || '',
                description: '',
              }
        }
      />
    </ModalWrapper>
  );
};

export default CreateTestGroupModal;